import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, updateData } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Checkbox } from "@/components/ui/checkbox";

export default function ChecklistPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const key = ["data", "checklist_items", activeId];
  const { data: items = [] } = useQuery({ queryKey: key, queryFn: () => listData("checklist_items", activeId), enabled: !!activeId });

  const m = useMutation({
    mutationFn: ({ id, done }) => updateData("checklist_items", id, { done }),
    onMutate: async ({ id, done }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (old) => old.map((t) => (t.id === id ? { ...t, done } : t)));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(key, ctx.prev),
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); },
  });

  const cats = [...new Set(items.map((i) => i.category))];

  return (
    <PageContainer>
      <PageHeader eyebrow="Foundation" title="Marketing Checklist"
        subtitle="Technical conversion, tracking and the first 5 high-ROI actions. Check items off as you complete them." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cats.map((cat) => {
          const list = items.filter((i) => i.category === cat);
          const done = list.filter((i) => i.done).length;
          return (
            <div key={cat} className="card-matte p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-semibold">{cat}</h3>
                <span className="text-xs text-[#7a7a7a]">{done}/{list.length}</span>
              </div>
              <div className="space-y-2.5">
                {list.map((it) => (
                  <label key={it.id} data-testid="checklist-item" className="flex items-start gap-3 cursor-pointer group">
                    <Checkbox checked={!!it.done} onCheckedChange={(v) => m.mutate({ id: it.id, done: !!v })}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#6B3DFF] data-[state=checked]:border-[#6B3DFF]" />
                    <span className={`text-sm ${it.done ? "line-through text-[#6a6a6a]" : "text-[#cfcfcf] group-hover:text-white"}`}>{it.task}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </PageContainer>
  );
}
